import { SupportUserAvailabilityOverride } from "@redotech/cockroachdb-codegen/tables/support-v2/support-user-availability-override";
import { isWithinHours } from "../is-within-hours";
import {
  WEEKDAY_KEYS,
  WorkScheduleAvailability,
  WorkScheduleReason,
  type LivePresence,
  type ScheduleOverride,
  type WorkSchedule,
  type WorkScheduleStatus,
} from "./types";

type TimeBlock = ScheduleOverride["timeBlocks"][number];

/** How far ahead to look for a state change before giving up. */
const TRANSITION_SEARCH_DAYS = 14;

const toZoned = (instant: Date, timezone: string): Temporal.ZonedDateTime =>
  Temporal.Instant.fromEpochMilliseconds(instant.getTime()).toZonedDateTimeISO(
    timezone,
  );

const tryPlainDate = (value: string): Temporal.PlainDate | null => {
  try {
    return Temporal.PlainDate.from(value);
  } catch {
    return null;
  }
};

const tryTimeOnDay = (
  day: Temporal.ZonedDateTime,
  time: string | undefined,
): Temporal.ZonedDateTime | null => {
  if (!time) {
    return null;
  }
  const [hour, minute] = time.split(":").map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return null;
  }
  if (hour! < 0 || hour! > 23 || minute! < 0 || minute! > 59) {
    return null;
  }
  try {
    return day.with({ hour, minute, second: 0, millisecond: 0 });
  } catch {
    return null;
  }
};

const overrideForDate = (
  schedule: WorkSchedule,
  at: Temporal.ZonedDateTime,
): ScheduleOverride | null => {
  const day = at.toPlainDate();
  return (
    schedule.scheduleOverrides.find((entry) => {
      const date = tryPlainDate(entry.date);
      return date !== null && Temporal.PlainDate.compare(day, date) === 0;
    }) ?? null
  );
};

/** Inclusive of `end`, matching `isWithinHours`. */
const isWithinBlocks = (
  blocks: readonly TimeBlock[],
  at: Temporal.ZonedDateTime,
): boolean =>
  blocks.some((block) => {
    const start = tryTimeOnDay(at, block.start);
    const end = tryTimeOnDay(at, block.end);
    if (!start || !end) {
      return false;
    }
    return (
      Temporal.ZonedDateTime.compare(start, at) <= 0 &&
      Temporal.ZonedDateTime.compare(at, end) <= 0
    );
  });

/** Active state ignoring the manual override. A date override wins over weekly hours. */
const deriveActiveAt = (
  schedule: WorkSchedule,
  at: Temporal.ZonedDateTime,
): boolean => {
  if (!schedule.scheduleEnabled) {
    return true;
  }
  const dateOverride = overrideForDate(schedule, at);
  if (dateOverride !== null) {
    return (
      dateOverride.available && isWithinBlocks(dateOverride.timeBlocks, at)
    );
  }
  return isWithinHours({
    availabilityEnabled: true,
    timezone: schedule.timezone,
    availableHours: schedule.weeklyHours,
    now: at,
  });
};

/** The blocks that actually govern a day: a date override's, else the weekday's. */
const blocksForDay = (
  schedule: WorkSchedule,
  day: Temporal.ZonedDateTime,
): readonly TimeBlock[] => {
  const dateOverride = overrideForDate(schedule, day);
  if (dateOverride !== null) {
    return dateOverride.available ? dateOverride.timeBlocks : [];
  }
  return (
    schedule.weeklyHours[WEEKDAY_KEYS[day.dayOfWeek % 7]!]?.timeBlocks ?? []
  );
};

/** Local midnights (override edges) plus every time block's edges, ascending. */
const candidateInstants = (
  schedule: WorkSchedule,
  startDay: Temporal.ZonedDateTime,
  days: number,
): Temporal.ZonedDateTime[] => {
  const candidates: Temporal.ZonedDateTime[] = [];
  let day = startDay.startOfDay();

  for (let i = 0; i <= days; i++) {
    candidates.push(day);

    for (const block of blocksForDay(schedule, day)) {
      const start = tryTimeOnDay(day, block.start);
      if (start) {
        candidates.push(start);
      }
      const end = tryTimeOnDay(day, block.end);
      if (end) {
        // `isWithinHours` is inclusive of `end`, so the flip lands a minute later.
        candidates.push(end.add({ minutes: 1 }));
      }
    }
    day = day.add({ days: 1 }).startOfDay();
  }

  return candidates.sort(Temporal.ZonedDateTime.compare);
};

/** Next derived-state flip; falls back to next local midnight so overrides always expire. */
export const nextTransitionAt = (schedule: WorkSchedule, now: Date): Date => {
  const zonedNow = toZoned(now, schedule.timezone);
  const current = deriveActiveAt(schedule, zonedNow);

  const upcoming = candidateInstants(
    schedule,
    zonedNow,
    TRANSITION_SEARCH_DAYS,
  ).filter((c) => Temporal.ZonedDateTime.compare(c, zonedNow) > 0);

  for (const candidate of upcoming) {
    if (deriveActiveAt(schedule, candidate) !== current) {
      return new Date(candidate.epochMilliseconds);
    }
  }

  return new Date(zonedNow.add({ days: 1 }).startOfDay().epochMilliseconds);
};

/** Most recent active-to-inactive flip at or before `now`, else null. */
export const lastShiftEndAt = (
  schedule: WorkSchedule,
  now: Date,
): Date | null => {
  if (!schedule.scheduleEnabled) {
    return null;
  }
  const zonedNow = toZoned(now, schedule.timezone);
  const past = candidateInstants(
    schedule,
    zonedNow.subtract({ days: TRANSITION_SEARCH_DAYS }),
    TRANSITION_SEARCH_DAYS,
  ).filter((c) => Temporal.ZonedDateTime.compare(c, zonedNow) <= 0);

  for (let i = past.length - 1; i >= 0; i--) {
    const candidate = past[i]!;
    if (
      !deriveActiveAt(schedule, candidate) &&
      deriveActiveAt(schedule, candidate.subtract({ minutes: 1 }))
    ) {
      return new Date(candidate.epochMilliseconds);
    }
  }

  return null;
};

/** On-shift state and the layer that decided it, before live presence applies. */
const shiftBaseline = (
  schedule: WorkSchedule,
  now: Date,
): { active: boolean; reason: WorkScheduleReason } => {
  const overrideInEffect =
    schedule.overrideStatus != null &&
    schedule.overrideExpiresAt != null &&
    schedule.overrideExpiresAt.getTime() > now.getTime();

  if (overrideInEffect) {
    return {
      active:
        schedule.overrideStatus === SupportUserAvailabilityOverride.Active,
      reason: WorkScheduleReason.MANUAL_OVERRIDE,
    };
  }

  if (!schedule.scheduleEnabled) {
    return { active: true, reason: WorkScheduleReason.NO_SCHEDULE };
  }

  const zonedNow = toZoned(now, schedule.timezone);
  const active = deriveActiveAt(schedule, zonedNow);

  if (overrideForDate(schedule, zonedNow) !== null) {
    return { active, reason: WorkScheduleReason.SCHEDULE_OVERRIDE };
  }

  return {
    active,
    reason: active
      ? WorkScheduleReason.WITHIN_HOURS
      : WorkScheduleReason.OUTSIDE_HOURS,
  };
};

/** Busy only downgrades: an already-offline user keeps the stickier reason. */
const withPresence = (
  active: boolean,
  presence: LivePresence | undefined,
): WorkScheduleAvailability =>
  !active
    ? WorkScheduleAvailability.OFFLINE
    : presence?.onCall
      ? WorkScheduleAvailability.BUSY
      : WorkScheduleAvailability.AVAILABLE;

/** Precedence: unexpired manual override, then date override, then weekly hours. */
export const isUserActive = (
  schedule: WorkSchedule,
  now: Date,
  presence?: LivePresence,
): WorkScheduleStatus => {
  const nextTransition = nextTransitionAt(schedule, now);
  // State the instant after the flip, so callers can tell a shift starting from
  // one ending without re-deriving the schedule themselves.
  const nextTransitionActive = deriveActiveAt(
    schedule,
    toZoned(new Date(nextTransition.getTime() + 1), schedule.timezone),
  );

  const { active, reason } = shiftBaseline(schedule, now);
  const availability = withPresence(active, presence);

  return {
    active: availability === WorkScheduleAvailability.AVAILABLE,
    availability,
    reason,
    nextTransitionAt: nextTransition,
    nextTransitionActive,
  };
};
